// Bottom-right per-player ping readout, scoreboard-style: hidden by
// default, shown while the user holds Tab (keyboard) or the View / Back /
// Select button on a gamepad (button index 8 in the standard mapping).
// Driven by the per-slot RTT the server measures via 2s ping/pong cycles
// and broadcasts in PlayerSlot.ping. AI slots show "AI".
//
// Mounts on document.body so it sits above the canvas regardless of the
// #hud container's stacking context.

import { TEAM_COLORS, type MatchState } from '../sim/MatchState';
import { t } from '../i18n';
import { makePingBadge, type PingBadgeHandle } from './components/PingBadge';

const STYLES_ID = 'ping-hud-styles';
const STYLES = `
@import url('https://fonts.googleapis.com/css2?family=Chewy&display=swap');

#ping-hud {
  position: fixed;
  right: 16px;
  bottom: 16px;
  z-index: 60;
  font-family: 'Chewy', ui-sans-serif, system-ui, sans-serif;
  background: rgba(24, 28, 36, 0.55);
  border: 1px solid rgba(255, 255, 255, 0.18);
  border-radius: 14px;
  padding: 10px 14px;
  display: flex; flex-direction: column; gap: 6px;
  box-shadow: 0 4px 8px rgba(0, 0, 0, 0.3);
  backdrop-filter: blur(8px);
  pointer-events: none;
  font-size: 16px;
  color: #FFFFFF;
  /* Hidden by default, faded in on Tab / gamepad-View hold. */
  opacity: 0;
  transform: translateX(8px);
  transition: opacity 140ms ease, transform 140ms ease;
}
#ping-hud.shown {
  opacity: 1;
  transform: translateX(0);
}
#ping-hud .row {
  display: flex; align-items: center; gap: 10px;
  min-width: 150px;
}
#ping-hud .dot {
  width: 10px; height: 10px; border-radius: 50%;
  background: var(--p-color, #cccccc);
  box-shadow: inset 0 -2px 0 rgba(0,0,0,0.18), 0 0 0 1px rgba(255,255,255,0.35);
}
#ping-hud .name {
  flex: 1;
  white-space: nowrap;
  overflow: hidden; text-overflow: ellipsis;
  max-width: 110px;
  text-shadow: 0 -2px 0 rgba(0, 60, 90, 0.4);
}
#ping-hud .badge-host {
  display: flex;
  justify-content: flex-end;
  min-width: 64px;
}
#ping-hud .you .name { color: #FFB84D; }
`;

export interface PingProvider {
  /** Returns RTT in ms for the given charId. 0 = unknown / AI. */
  pingFor(charId: number): number;
}

/** Standard-mapping gamepad button index for the View / Back / Select
 *  button — the conventional scoreboard binding on console gamepads. */
const GAMEPAD_VIEW_BUTTON = 8;

export class PingHud {
  private root: HTMLElement;
  private rows: Map<number, { badge: PingBadgeHandle; isLocal: boolean; lastNonZeroAt: number }> = new Map();
  private host: HTMLElement;
  private startedAt = performance.now();
  private keyHeld = false;
  private padHeld = false;
  private padPollHandle = 0;
  private onKeyDown: (e: KeyboardEvent) => void;
  private onKeyUp: (e: KeyboardEvent) => void;
  private onBlur: () => void;

  constructor(private match: MatchState, private provider: PingProvider) {
    ensureStyles();
    this.host = ensureHostContainer();

    this.root = document.createElement('div');
    this.root.id = 'ping-hud';

    // Build one row per slot, sorted by team then charId so the order is
    // stable across joins/leaves.
    const ordered = [...match.members].sort((a, b) => {
      const ta = a.team === 'red' ? 0 : 1;
      const tb = b.team === 'red' ? 0 : 1;
      return ta - tb || a.id - b.id;
    });
    for (const m of ordered) {
      const teamColor = TEAM_COLORS[m.team].primary;
      const row = document.createElement('div');
      row.className = 'row' + (m.isLocal ? ' you' : '');
      row.innerHTML = `
        <span class="dot" style="--p-color: ${teamColor};"></span>
        <span class="name"></span>
        <span class="badge-host"></span>
      `;
      const name = row.querySelector('.name') as HTMLElement;
      const badgeHost = row.querySelector('.badge-host') as HTMLElement;
      name.textContent = (m.isLocal ? t('hud.you') + ' · ' : '') + m.name;
      const badge = makePingBadge();
      badgeHost.appendChild(badge.el);
      this.rows.set(m.id, { badge, isLocal: m.isLocal, lastNonZeroAt: 0 });
      this.root.appendChild(row);
    }
    this.host.appendChild(this.root);

    // Show-on-Tab. Capture phase so we win over any focusable element's
    // default Tab handling (which would shift focus to the next form
    // element). Release on keyup OR window blur (alt-tab away leaves the
    // panel up otherwise).
    this.onKeyDown = (e: KeyboardEvent): void => {
      if (e.code !== 'Tab' || e.repeat) return;
      e.preventDefault();
      this.keyHeld = true;
      this.refreshVisible();
    };
    this.onKeyUp = (e: KeyboardEvent): void => {
      if (e.code !== 'Tab') return;
      this.keyHeld = false;
      this.refreshVisible();
    };
    this.onBlur = (): void => {
      if (!this.keyHeld && !this.padHeld) return;
      this.keyHeld = false;
      this.padHeld = false;
      this.refreshVisible();
    };
    addEventListener('keydown', this.onKeyDown, { capture: true });
    addEventListener('keyup', this.onKeyUp, { capture: true });
    addEventListener('blur', this.onBlur);

    // Show-on-gamepad-View. Polled per rAF; cheap (one getGamepads scan +
    // a button index lookup per frame).
    const pollPad = (): void => {
      const pads = navigator.getGamepads ? navigator.getGamepads() : [];
      let down = false;
      for (const p of pads) {
        if (!p) continue;
        const btn = p.buttons[GAMEPAD_VIEW_BUTTON];
        if (btn && btn.pressed) { down = true; break; }
      }
      if (down !== this.padHeld) {
        this.padHeld = down;
        this.refreshVisible();
      }
      this.padPollHandle = requestAnimationFrame(pollPad);
    };
    this.padPollHandle = requestAnimationFrame(pollPad);
  }

  private refreshVisible(): void {
    const visible = this.keyHeld || this.padHeld;
    this.root.classList.toggle('shown', visible);
  }

  /** Call once per render frame. Cheap — PingBadge.setPing is idempotent
   *  when the ms / AI state hasn't changed. */
  update(): void {
    const now = performance.now();
    for (const m of this.match.members) {
      const row = this.rows.get(m.id);
      if (!row) continue;
      const ping = this.provider.pingFor(m.id);
      // AI heuristic: a non-local slot whose ping has stayed at 0 for
      // ~3s after this HUD mounted is treated as AI. Local slots always
      // expect a measurement (server pings everyone).
      let isAI = false;
      if (ping > 0) {
        row.lastNonZeroAt = now;
      } else if (!row.isLocal && (now - this.startedAt) > 3_000 && row.lastNonZeroAt === 0) {
        isAI = true;
      }
      row.badge.setPing(ping, isAI);
    }
  }

  dispose(): void {
    if (this.padPollHandle) cancelAnimationFrame(this.padPollHandle);
    this.padPollHandle = 0;
    removeEventListener('keydown', this.onKeyDown, { capture: true } as EventListenerOptions);
    removeEventListener('keyup', this.onKeyUp, { capture: true } as EventListenerOptions);
    removeEventListener('blur', this.onBlur);
    for (const r of this.rows.values()) r.badge.dispose();
    this.root.remove();
    this.rows.clear();
  }
}

function ensureStyles(): void {
  if (document.getElementById(STYLES_ID)) return;
  const s = document.createElement('style');
  s.id = STYLES_ID;
  s.textContent = STYLES;
  document.head.appendChild(s);
}

function ensureHostContainer(): HTMLElement {
  // Mount on body so it sits above the canvas regardless of the #hud
  // container's stacking context.
  return document.body;
}
